import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Receipt, CheckCircle2, AlertCircle, Mail, Clock, Download } from "lucide-react";
import { getMyLlbReceiptsAndAudit } from "@/lib/api/llb-tutoring.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/portal/llb-receipts")({
  head: () => ({
    meta: [
      { title: "LLB tutoring — receipts & audit | Student portal" },
      { name: "description", content: "Your PayFast confirmations, payment events and module access log for UNISA LLB tutoring." },
    ],
  }),
  component: LlbReceiptsPage,
});

function fmt(n: number | string | null | undefined) {
  const v = Number(n ?? 0);
  return `R${v.toFixed(2)}`;
}
function ts(s: string | null | undefined) {
  return s ? new Date(s).toLocaleString("en-ZA") : "—";
}

function csvCell(v: any) {
  if (v == null) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}
function downloadCsv(filename: string, rows: any[], columns: { key: string; label: string }[]) {
  const header = columns.map((c) => csvCell(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvCell(typeof r[c.key] === "object" ? JSON.stringify(r[c.key]) : r[c.key])).join(","))
    .join("\n");
  const blob = new Blob([`${header}\n${body}\n`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function LlbReceiptsPage() {
  const load = useServerFn(getMyLlbReceiptsAndAudit);
  const q = useQuery({ queryKey: ["my-llb-receipts"], queryFn: () => load(), refetchInterval: 60_000 });

  if (q.isLoading) return <div className="container-narrow py-10 text-sm text-muted-foreground">Loading…</div>;
  const d = q.data;
  if (!d) return <div className="container-narrow py-10 text-sm text-rose-700">Could not load receipts.</div>;

  const orders = d.orders as any[];
  const events = d.events as any[];
  const enrolments = d.enrolments as any[];
  const downloads = (d as any).downloads as any[] ?? [];

  function exportOrders() {
    downloadCsv(
      `llb-orders-${Date.now()}.csv`,
      orders.map((o) => ({ ...o, module_codes: (o.module_codes ?? []).join(" ") })),
      [
        { key: "reference", label: "Reference" },
        { key: "semester", label: "Semester" },
        { key: "module_count", label: "Modules" },
        { key: "module_codes", label: "Module codes" },
        { key: "subtotal_zar", label: "Subtotal (R)" },
        { key: "discount_zar", label: "Discount (R)" },
        { key: "amount_zar", label: "Total (R)" },
        { key: "status", label: "Status" },
        { key: "created_at", label: "Created" },
        { key: "paid_at", label: "Paid" },
      ],
    );
  }
  function exportEvents() {
    downloadCsv(`llb-payment-events-${Date.now()}.csv`, events, [
      { key: "reference", label: "Reference" },
      { key: "provider", label: "Provider" },
      { key: "event_type", label: "Event" },
      { key: "processed", label: "Processed" },
      { key: "error", label: "Error" },
      { key: "created_at", label: "Created" },
      { key: "processed_at", label: "Processed at" },
    ]);
  }
  function exportEnrolments() {
    downloadCsv(`llb-module-access-${Date.now()}.csv`, enrolments, [
      { key: "module_code", label: "Module" },
      { key: "module_name", label: "Name" },
      { key: "status", label: "Status" },
      { key: "started_at", label: "Started" },
      { key: "completed_at", label: "Completed" },
      { key: "created_at", label: "Enrolled" },
    ]);
  }
  function exportDownloads() {
    downloadCsv(`llb-downloads-${Date.now()}.csv`, downloads, [
      { key: "module_code", label: "Module" },
      { key: "asset", label: "Asset" },
      { key: "asset_label", label: "Asset label" },
      { key: "created_at", label: "Downloaded" },
    ]);
  }

  return (
    <div className="container-narrow py-10 space-y-8">
      <header className="flex items-start gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-md bg-navy/5 text-navy">
          <Receipt className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl text-navy md:text-3xl">LLB tutoring — receipts &amp; audit</h1>
          <p className="text-sm text-muted-foreground">
            Every PayFast confirmation, ITN event, download, and module-access record tied to your enrolments.
          </p>
        </div>
      </header>

      <section className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg text-navy">My orders</h2>
          <Button size="sm" variant="outline" disabled={!orders.length} onClick={exportOrders}>
            <Download className="h-3 w-3 mr-1"/>Export CSV
          </Button>
        </div>
        {orders.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No tutoring orders yet.</p>
        ) : (
          <table className="mt-3 w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr><th className="py-2">Reference</th><th>Modules</th><th>Total</th><th>Status</th><th>Created</th><th>Paid</th><th></th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {orders.map((o: any) => (
                <tr key={o.id} className="align-top">
                  <td className="py-2 font-mono">{o.reference}</td>
                  <td className="py-2">{o.module_count} · <span className="font-mono">{(o.module_codes ?? []).join(", ")}</span></td>
                  <td className="py-2 tabular-nums">{fmt(o.amount_zar)}</td>
                  <td className="py-2">
                    {o.status === "paid" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3"/>Paid</span>
                    ) : (
                      <Link to="/payment-status/$ref" params={{ ref: o.reference }} className="text-amber-700 underline">Pending</Link>
                    )}
                  </td>
                  <td className="py-2">{ts(o.created_at)}</td>
                  <td className="py-2">{ts(o.paid_at)}</td>
                  <td className="py-2 text-right">
                    <Link to="/invoice/$ref" params={{ ref: o.reference }} className="text-navy underline">View / receipt</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg text-navy">PayFast payment events</h2>
          <Button size="sm" variant="outline" disabled={!events.length} onClick={exportEvents}>
            <Download className="h-3 w-3 mr-1"/>Export CSV
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">ITN notifications from PayFast for your references.</p>
        {events.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No events recorded yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-border text-xs">
            {events.map((e: any) => (
              <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="font-mono">{e.reference}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 uppercase tracking-wider text-[10px]">{e.event_type}</span>
                <span className={e.processed ? "text-emerald-700" : "text-amber-700"}>
                  {e.processed ? "processed" : "queued"}
                </span>
                {e.error ? <span className="text-rose-700">{e.error}</span> : null}
                <span className="text-muted-foreground">{ts(e.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-border bg-card p-5">
        <h2 className="font-display text-lg text-navy"><Mail className="inline h-4 w-4 mr-1"/>Receipt email delivery</h2>
        {d.receiptJobs.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No queued receipt emails for your orders.</p>
        ) : (
          <ul className="mt-3 divide-y divide-border text-xs">
            {d.receiptJobs.map((j: any, i: number) => (
              <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="font-mono">{j.reference}</span>
                <span>status: <strong>{j.status}</strong> · attempts {j.attempts}</span>
                <span>sent {ts(j.sent_at)}</span>
                {j.last_error ? <span className="text-rose-700">{j.last_error}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg text-navy"><Clock className="inline h-4 w-4 mr-1"/>Module access log</h2>
          <Button size="sm" variant="outline" disabled={!enrolments.length} onClick={exportEnrolments}>
            <Download className="h-3 w-3 mr-1"/>Export CSV
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Your enrolment history for tutored modules.</p>
        {enrolments.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No enrolments yet.</p>
        ) : (
          <table className="mt-3 w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr><th className="py-2">Module</th><th>Status</th><th>Started</th><th>Completed</th><th>Enrolled</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {enrolments.map((e: any, i: number) => (
                <tr key={i}>
                  <td className="py-2"><span className="font-mono">{e.module_code}</span> — {e.module_name}</td>
                  <td className="py-2">
                    {e.status === "enrolled"
                      ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3"/>{e.status}</span>
                      : <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle className="h-3 w-3"/>{e.status}</span>}
                  </td>
                  <td className="py-2">{ts(e.started_at)}</td>
                  <td className="py-2">{ts(e.completed_at)}</td>
                  <td className="py-2">{ts(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg text-navy"><Download className="inline h-4 w-4 mr-1"/>Download history</h2>
          <Button size="sm" variant="outline" disabled={!downloads.length} onClick={exportDownloads}>
            <Download className="h-3 w-3 mr-1"/>Export CSV
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Notes &amp; templates you downloaded from module details.</p>
        {downloads.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No downloads yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-border text-xs">
            {downloads.map((dl: any) => (
              <li key={dl.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="font-mono">{dl.module_code}</span>
                <span>{dl.asset_label || dl.asset}</span>
                <span className="text-muted-foreground">{ts(dl.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
